import { forwardRef } from "react";
import styled from "styled-components/macro";
import { palette } from "../../styles/constants";
import { cn } from "../../util/css";

type MerchantLogoProps = {
    src?: string;
    alt: string;
    size?: "sm" | "md";
    style?: React.CSSProperties & {
        "--placeholder-color"?: string;
        "--placeholder-bg"?: string;
    };
};

export default function MerchantLogo({
    src,
    alt,
    style,
    size = "sm",
}: MerchantLogoProps) {
    return (
        <Wrapper
            className={cn(
                !src && "placeholder",
                size === "sm" && "small",
                size === "md" && "medium",
            )}
            style={style}
        >
            {src ? <Img src={src} alt={alt} /> : <PlaceholderIcon />}
        </Wrapper>
    );
}

const Wrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    &.placeholder {
        background: var(--placeholder-bg, ${palette.darkmode[200]});
        color: var(--placeholder-color, ${palette.neutral[400]});
    }
    &.small {
        width: 32px;
        height: 32px;
        border-radius: 8px;
    }
    &.medium {
        width: 48px;
        height: 48px;
        border-radius: 12px;
        svg {
            width: 32px;
            height: 32px;
        }
    }
`;

const Img = styled.img`
    width: 100%;
    height: 100%;
    border-radius: 8px;
    object-fit: cover;
`;

const PlaceholderIcon = forwardRef<
    SVGSVGElement,
    React.SVGProps<SVGSVGElement>
>((props, ref) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 20 20"
        width="20"
        height="20"
        fill="none"
        {...props}
        ref={ref}
    >
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M4.75 4.75C4.75 3.7835 5.5335 3 6.5 3H13.5C14.4665 3 15.25 3.7835 15.25 4.75V15.25C15.7332 15.25 16.125 15.6418 16.125 16.125C16.125 16.6082 15.7332 17 15.25 17H12.75C12.1977 17 11.75 16.5523 11.75 16V14.375C11.75 13.8918 11.3583 13.5 10.875 13.5H9.125C8.64175 13.5 8.25 13.8918 8.25 14.375V16C8.25 16.5523 7.80228 17 7.25 17H4.75C4.26675 17 3.875 16.6082 3.875 16.125C3.875 15.6418 4.26675 15.25 4.75 15.25V4.75ZM7.375 5.625H9.125V7.375H7.375V5.625ZM9.125 9.125H7.375V10.875H9.125V9.125ZM10.875 5.625H12.625V7.375H10.875V5.625ZM12.625 9.125H10.875V10.875H12.625V9.125Z"
            fill="currentColor"
        />
    </svg>
));

PlaceholderIcon.displayName = "PlaceholderIcon";
